import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import api from "../api";
import Icon from "../lib/icon";
import ApiImage from "../components/ApiImage";

import "./scss/manager-product.scss";
import ProductStatusBadge from "../components/ProductStatusBadge";
import BackButton from "../components/BackButton";
import Breadcrumbs from "../components/Breadcrumbs";
import BottomSheet from "../components/BottomSheet/BottomSheet";
import Button from "../components/Button";
import { useToast } from "../contexts/ToastContext";

export default function ManagerProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast, showConfirm } = useToast();

  const [product, setProduct] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectLoading, setRejectLoading] = useState(false);

  useEffect(() => {
    load();
  }, [id]);

  async function load() {
    const { data } = await api.get(`/api/manager/products/${id}`);
    setProduct(data);
  }

  async function handleApprove() {
    showConfirm({
      message: "Вы уверены, что хотите опубликовать товар?",
      confirmText: "Опубликовать",
      cancelText: "Отмена",
      onConfirm: async () => {
        try {
          showToast("Публикация...", "loading");

          await api.post(`/api/manager/products/${id}/approve`);

          showToast("Товар опубликован", "info");
          load();
        } catch {
          showToast("Ошибка публикации");
        }
      }
    });
  }


  async function handleRejectSubmit() {
    if (!rejectReason.trim()) return;

    try {
      setRejectLoading(true);

      await api.post(`/api/manager/products/${id}/reject`, {
        reason: rejectReason
      });
      showToast("Товар отклонён", "info");
      setRejectOpen(false);
      setRejectReason("");
      load();
    } 
    catch (e) {
      showToast(e?.response?.data || "Ошибка отклонения");
    }
    finally {
      setRejectLoading(false);
    }
  }

  if (!product) return null;

  const gallery = product.photos?.length
    ? product.photos.map(p => p.image)
    : [];

  const allSpecs = [
    ...(product.specifications || []),
    ...(product.filters || [])
  ];

  return (
    <div className="manager-product">
      
      {/* HEADER */}
      <div className="manager-product__header">
        <BackButton />
        
        <Breadcrumbs
          items={[
            { label: "Главная", path: "/manager/dashboard" },
            { label: "Товар",  path: "/manager/products" },
            { label: product.name }
          ]}
        />
      </div>

      {/* GALLERY */}
      <div className="manager-product__gallery">
        <Swiper
          modules={[Pagination]}
          pagination={{
            clickable: true,
            bulletClass: "manager-product__dot",
            bulletActiveClass: "manager-product__dot--active",
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
        >
          {gallery.map((img, i) => (
            <SwiperSlide key={i}>
              <div className="manager-product__gallery-image">
                <ApiImage src={img} />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="manager-product__thumbs">
          {gallery.map((img, i) => (
            <div
              key={i}
              className={`manager-product__thumb ${
                activeIndex === i ? "active" : ""
              }`}
            >
              <ApiImage src={img} />
            </div>
          ))}
        </div>
      </div>

      {/* INFO */}
      <div className="manager-product__info">
        <ProductStatusBadge status={product.status} />

        <div className="manager-product__title">
          {product.name}
        </div>

        <div className="manager-product__rating">
          <Icon name="star-filled" width={14} height={14} color="#FE9402" />
          <span>{product.rating ?? 0}</span>
          <span className="count">
            {product.reviewCount ?? 0} оценок
          </span>
        </div>

        <div className="manager-product__price-row">
          <div className="manager-product__price">
            {product.isDiscount ? (
              <>
                <span className="discount">
                  {product.costDiscount} ₽
                </span>
                <span className="old">
                  {product.cost} ₽
                </span>
              </>
            ) : (
              <span>{product.cost} ₽</span>
            )}
          </div>

          {product.bonus > 0 && (
            <div className="manager-product__bonus">
              <Icon name="coin" width={14} height={14} />
              {product.bonus}
            </div>
          )}
        </div>
        {product.status === "cancel" && product.comment && (
          <div className="manager-product__reject-reason">
            <div className="label">Причина отклонения</div>
            <div className="text">{product.comment}</div>
          </div>
        )}
        <button
          className="manager-product__approve"
          onClick={handleApprove}
        >
          ✓ Опубликовать
        </button>

        <button
          className="manager-product__reject"
          onClick={() => setRejectOpen(true)}
        >
          Отклонить
        </button>
      </div>

      {/* SELLER */}
      {product.marketName && (
        <div className="manager-product__card">
          <div className="manager-product__seller">
            <div className="avatar">
              {product.marketName[0]}
            </div>
            <div>
              <div className="name">{product.marketName}</div>
            </div>
          </div>
        </div>
      )}

      {/* DESCRIPTION */}
      <div className="manager-product__card">
        <div className="title">Описание</div>
        <div className="text">{product.description}</div>
      </div>

      {/* SPECIFICATIONS */}
      {allSpecs.length > 0 && (
        <div className="manager-product__card">
          <div className="title">Характеристики</div>

          {allSpecs.map((s, i) => (
            <div key={i} className="manager-product__spec-row">
              <span>{s.name || s.filterName}</span>
              <span>{s.label || s.parameter}</span>
            </div>
          ))}
        </div>
      )}
    <BottomSheet
      open={rejectOpen}
      onClose={() => setRejectOpen(false)}
      header="Опишите причину отклонения"
      closeButtomShow={true}
    >
      <div className="reject-sheet">

        <textarea
          className="reject-sheet__textarea"
          placeholder="Причина"
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
        />

        <Button
          className="btn-primary large"
          onClick={handleRejectSubmit}
          load={rejectLoading}
          disabled={!rejectReason.trim()}
        >
          Отклонить
        </Button>

      </div>
    </BottomSheet>
    </div>
  );
}